import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Box,
  Check,
  CheckCircle2,
  ChevronDown,
  Circle,
  FileDiff,
  Info,
  LoaderCircle,
  PackageCheck,
  PackagePlus,
  Search,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Store,
  Tag,
  Truck,
  UploadCloud,
  XCircle,
} from "lucide-react";
import type { PageContext } from "../App";
import { api, getApiError } from "../api";
import {
  getBlockedFulfillmentModes,
  getCatalogSelectionDraft,
  getDuplicateOfferError,
} from "../catalogOfferGuard";
import {
  Button,
  Field,
  InlineError,
  Input,
  MarketplaceMark,
  MarketplaceSelector,
  Panel,
  Select,
  StatePanel,
  StatusPill,
  formatMoney,
} from "../components/ui";
import { ProductTypeAttributesForm } from "../components/ProductTypeAttributesForm";
import { SkuIdentityFields } from "../components/SkuIdentityFields";
import { useAsync } from "../hooks";
import {
  buildPtdFieldModel,
  getApplicableTemplateAttributes,
  missingRequiredPtdFields,
  resolveProductTypeSchema,
} from "../productTypeSchema";
import {
  MARKETPLACE_CODES,
  MARKETPLACE_META,
  type AppSettings,
  type CatalogItem,
  type FulfillmentMode,
  type ListingDraft,
  type ListingMode,
  type ListingPreview,
  type MarketplaceCode,
  type ProductType,
  type ProductTypeDefinitionResponse,
  type Restriction,
  type SettingsTemplate,
  type ShippingTemplate,
} from "../types";

const STEPS = [
  { id: 1, short: "Katalog", label: "Katalog & Modus" },
  { id: 2, short: "Angebot", label: "Artikeldaten" },
  { id: 3, short: "Länder", label: "Länder & Startpreise" },
  { id: 4, short: "Prüfen", label: "Vorschau & Senden" },
] as const;

function emptyDraft(): ListingDraft {
  return {
    mode: "MATCH_EXISTING_ASIN",
    sellerSku: "",
    condition: "new_new",
    targetMarketplaces: ["DE"],
    fulfillmentMode: "FBM",
    startPrices: {},
    shippingTemplates: {},
    attributes: {},
    mediaLocators: [],
  };
}

export function UploaderPage({ notify }: PageContext) {
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<ListingDraft>(emptyDraft);
  const [selectedCatalogItem, setSelectedCatalogItem] = useState<CatalogItem | null>(null);
  const [restrictions, setRestrictions] = useState<Restriction[]>([]);
  const [productType, setProductType] = useState<ProductType | null>(null);
  const [productTypeDefinition, setProductTypeDefinition] =
    useState<ProductTypeDefinitionResponse | null>(null);
  const [productTypeDefinitionLoading, setProductTypeDefinitionLoading] = useState(false);
  const [productTypeDefinitionError, setProductTypeDefinitionError] =
    useState<string | null>(null);
  const productTypeRequestId = useRef(0);
  const [preview, setPreview] = useState<ListingPreview | null>(null);
  const [submitResult, setSubmitResult] = useState<{ jobId: string; status: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const previewRequest = useRef(0);
  useEffect(() => () => { previewRequest.current += 1; }, []);
  const settings = useAsync(api.settings, []);
  const templates = useAsync(api.templates, []);
  const duplicateOfferError = getDuplicateOfferError(draft, selectedCatalogItem);

  const updateDraft = (patch: Partial<ListingDraft>) => {
    previewRequest.current += 1;
    setPreviewing(false);
    setDraft((current) => ({ ...current, ...patch, sellerSku: "" }));
    setPreview(null);
    setSubmitResult(null);
    setActionError(null);
  };

  const chooseMode = (mode: ListingMode) => {
    productTypeRequestId.current += 1;
    updateDraft({
      mode,
      asin: undefined,
      productType: undefined,
      attributes: {},
      ...(mode === "CREATE_NEW_ASIN"
        ? {
            targetMarketplaces: ["DE"] as MarketplaceCode[],
            startPrices:
              typeof draft.startPrices.DE === "number"
                ? { DE: draft.startPrices.DE }
                : {},
            shippingTemplates: draft.shippingTemplates.DE
              ? { DE: draft.shippingTemplates.DE }
              : {},
          }
        : {}),
    });
    setSelectedCatalogItem(null);
    setProductType(null);
    setProductTypeDefinition(null);
    setProductTypeDefinitionError(null);
    setProductTypeDefinitionLoading(false);
    setRestrictions([]);
  };

  const loadProductTypeDefinition = async (name: string) => {
    const requestId = ++productTypeRequestId.current;
    setProductTypeDefinitionLoading(true);
    setProductTypeDefinitionError(null);
    try {
      const definition = await api.productType(name, "DE");
      if (requestId !== productTypeRequestId.current) return;
      setProductTypeDefinition(definition);
    } catch (error) {
      if (requestId !== productTypeRequestId.current) return;
      setProductTypeDefinition(null);
      setProductTypeDefinitionError(getApiError(error));
    } finally {
      if (requestId === productTypeRequestId.current) {
        setProductTypeDefinitionLoading(false);
      }
    }
  };

  const resolvedProductTypeSchema = useMemo(
    () => resolveProductTypeSchema(productTypeDefinition),
    [productTypeDefinition],
  );
  const productTypeFieldModel = useMemo(
    () =>
      productTypeDefinition && resolvedProductTypeSchema
        ? buildPtdFieldModel(productTypeDefinition, resolvedProductTypeSchema)
        : null,
    [productTypeDefinition, resolvedProductTypeSchema],
  );

  const canContinue = useMemo(() => {
    if (duplicateOfferError) return false;
    if (step === 1) {
      return draft.mode === "MATCH_EXISTING_ASIN"
        ? Boolean(draft.asin && selectedCatalogItem)
        : Boolean(draft.productType);
    }
    if (step === 2) {
      if (!draft.skuUnit) return false;
      if (draft.mode === "CREATE_NEW_ASIN") {
        if (productTypeFieldModel) {
          return (
            missingRequiredPtdFields(productTypeFieldModel, draft.attributes).length === 0
          );
        }
        return Boolean(
          (draft.attributes.item_name || draft.attributes.item_name_keyword) &&
            draft.attributes.brand &&
            (draft.attributes.external_product_id ||
              draft.attributes.externally_assigned_product_identifier),
        );
      }
      return true;
    }
    if (step === 3) {
      if (!draft.targetMarketplaces.length) return false;
      return draft.targetMarketplaces.every((code) => {
        const price = draft.startPrices[code];
        const shippingOkay =
          draft.fulfillmentMode === "FBA" || Boolean(draft.shippingTemplates[code]);
        return typeof price === "number" && price > 0 && shippingOkay;
      });
    }
    return true;
  }, [draft, duplicateOfferError, productTypeFieldModel, selectedCatalogItem, step]);

  const goNext = () => {
    setActionError(null);
    if (!canContinue) {
      setActionError(duplicateOfferError ?? "Bitte vervollständige zuerst alle Pflichtangaben in diesem Schritt.");
      return;
    }
    setStep((current) => Math.min(4, current + 1));
  };

  const createPreview = async () => {
    if (duplicateOfferError) {
      setActionError(duplicateOfferError);
      return;
    }
    const requestId = ++previewRequest.current;
    setPreviewing(true);
    setActionError(null);
    try {
      const result = await api.previewListing(draft);
      if (requestId !== previewRequest.current) return;
      setDraft(result.draft);
      setPreview(result);
      if (!result.canSubmit) {
        setActionError("Die Vorschau enthält Fehler. Behebe sie, bevor du an Amazon sendest.");
      }
    } catch (error) {
      if (requestId !== previewRequest.current) return;
      setActionError(getApiError(error));
    } finally {
      if (requestId === previewRequest.current) setPreviewing(false);
    }
  };

  const submit = async () => {
    if (duplicateOfferError) {
      setActionError(duplicateOfferError);
      return;
    }
    if (!preview) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const result = await api.submitListing({
        previewId: preview.previewId,
        confirmationToken: preview.confirmationToken,
        idempotencyKey: `listing-${preview.previewId}`,
      });
      setSubmitResult(result);
      notify(`Listing-Job ${result.jobId} wurde sicher eingereiht.`, "success");
    } catch (error) {
      setActionError(getApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    previewRequest.current += 1;
    setPreviewing(false);
    setDraft(emptyDraft());
    setStep(1);
    setSelectedCatalogItem(null);
    setProductType(null);
    productTypeRequestId.current += 1;
    setProductTypeDefinition(null);
    setProductTypeDefinitionError(null);
    setProductTypeDefinitionLoading(false);
    setRestrictions([]);
    setPreview(null);
    setSubmitResult(null);
    setActionError(null);
  };

  return (
    <div className="uploader-page">
      <header className="wizard-header">
        <div>
          <p className="eyebrow">Amazon Uploader</p>
          <h1>Artikel kontrolliert veröffentlichen</h1>
          <p className="page-description">
            Katalogtreffer wählen oder einen neuen Artikel erstellen. Vor dem Senden siehst
            du für jedes Land exakt, was geändert wird.
          </p>
        </div>
        <span className="safe-chip"><ShieldCheck size={16} /> Sicherer Vorschau-Modus</span>
      </header>

      <ol className="wizard-steps">
        {STEPS.map((item) => (
          <li
            key={item.id}
            className={`${step === item.id ? "is-current" : ""} ${step > item.id ? "is-done" : ""}`}
          >
            <button
              type="button"
              onClick={() => {
                if (item.id <= step) {
                  setStep(item.id);
                  setActionError(null);
                }
              }}
              disabled={item.id > step}
            >
              <span className="wizard-step__number">
                {step > item.id ? <Check size={15} /> : item.id}
              </span>
              <span><small>{item.short}</small><strong>{item.label}</strong></span>
            </button>
          </li>
        ))}
      </ol>

      {(actionError || duplicateOfferError) && (
        <div className="alert alert--error uploader-alert" role="alert">
          <AlertTriangle size={18} />
          <span>{actionError ?? duplicateOfferError}</span>
        </div>
      )}

      <div className="wizard-layout">
        <div className="wizard-main">
          {step === 1 && (
            <CatalogStep
              draft={draft}
              chooseMode={chooseMode}
              selectedItem={selectedCatalogItem}
              setSelectedItem={(item, itemRestrictions) => {
                const nextDraft = getCatalogSelectionDraft(draft, item);
                if (!nextDraft) {
                  setActionError("Für diese ASIN sind FBM und FBA auf Amazon DE bereits vorhanden. Ein weiterer Artikel kann nicht angelegt werden.");
                  return;
                }
                setSelectedCatalogItem(item);
                setRestrictions(itemRestrictions);
                updateDraft(nextDraft);
              }}
              productType={productType}
              setProductType={(next) => {
                setProductType(next);
                updateDraft({ productType: next.name, attributes: {} });
                void loadProductTypeDefinition(next.name);
              }}
            />
          )}
          {step === 2 && (
            <OfferStep
              draft={draft}
              updateDraft={updateDraft}
              existingOffers={
                draft.mode === "MATCH_EXISTING_ASIN" && selectedCatalogItem && selectedCatalogItem.asin === draft.asin
                  ? selectedCatalogItem.existingOffers
                  : undefined
              }
              templates={templates.data ?? []}
              templatesLoading={templates.loading}
              productTypeDefinition={productTypeDefinition}
              productTypeDefinitionLoading={productTypeDefinitionLoading}
              productTypeDefinitionError={productTypeDefinitionError}
              reloadProductTypeDefinition={() => {
                if (draft.productType) void loadProductTypeDefinition(draft.productType);
              }}
            />
          )}
          {step === 3 && (
            <MarketplaceStep
              draft={draft}
              updateDraft={updateDraft}
              settings={settings.data}
              settingsError={settings.error}
            />
          )}
          {step === 4 && (
            <PreviewStep
              draft={draft}
              preview={preview}
              previewing={previewing}
              submitting={submitting}
              submitResult={submitResult}
              blocked={Boolean(duplicateOfferError)}
              onPreview={createPreview}
              onSubmit={submit}
              onReset={reset}
            />
          )}
        </div>

        <aside className="wizard-summary">
          <h2>Upload-Übersicht</h2>
          <SummaryRow
            label="Modus"
            value={draft.mode === "MATCH_EXISTING_ASIN" ? "Katalog-ASIN" : "Neue ASIN"}
            complete
          />
          <SummaryRow
            label="Artikel"
            value={
              selectedCatalogItem?.title ??
              String(draft.attributes.item_name ?? draft.productType ?? "Noch nicht gewählt")
            }
            complete={Boolean(selectedCatalogItem || draft.productType)}
          />
          <SummaryRow
            label="SKU"
            value={draft.sellerSku || "Noch offen"}
            complete={Boolean(draft.sellerSku)}
          />
          <SummaryRow
            label="Versand"
            value={draft.fulfillmentMode}
            complete={step > 1}
          />
          <SummaryRow
            label="Zielländer"
            value={draft.targetMarketplaces.join(", ")}
            complete={draft.targetMarketplaces.length > 0}
          />
          <div className="summary-safety">
            <ShieldCheck size={18} />
            <div>
              <strong>Bestandsschutz aktiv</strong>
              <p>
                Neu + FBM startet mit 0. Bestehende Mengen und sämtliche FBA-Bestände
                werden nicht geschrieben.
              </p>
            </div>
          </div>
          <div className="summary-safety summary-safety--blue">
            <Tag size={18} />
            <div>
              <strong>Nur Startpreise</strong>
              <p>Nach dem Upload übernimmt wieder der bestehende Repricer.</p>
            </div>
          </div>
        </aside>
      </div>

      {step < 4 && (
        <footer className="wizard-footer">
          <Button
            variant="ghost"
            icon={<ArrowLeft size={17} />}
            disabled={step === 1}
            onClick={() => {
              setStep((current) => Math.max(1, current - 1));
              setActionError(null);
            }}
          >
            Zurück
          </Button>
          <span>Schritt {step} von 4</span>
          <Button onClick={goNext} disabled={Boolean(duplicateOfferError)}>
            Weiter <ArrowRight size={17} />
          </Button>
        </footer>
      )}
    </div>
  );
}

function CatalogStep({
  draft,
  chooseMode,
  selectedItem,
  setSelectedItem,
  productType,
  setProductType,
}: {
  draft: ListingDraft;
  chooseMode: (mode: ListingMode) => void;
  selectedItem: CatalogItem | null;
  setSelectedItem: (item: CatalogItem, restrictions: Restriction[]) => void;
  productType: ProductType | null;
  setProductType: (type: ProductType) => void;
}) {
  return (
    <Panel className="wizard-panel">
      <div className="wizard-panel__header">
        <div>
          <span className="section-number">01</span>
          <p className="eyebrow">Katalogweg wählen</p>
          <h2>Existiert der Artikel schon bei Amazon?</h2>
        </div>
      </div>
      <div className="mode-grid">
        <button
          type="button"
          className={`choice-card ${draft.mode === "MATCH_EXISTING_ASIN" ? "is-selected" : ""}`}
          onClick={() => chooseMode("MATCH_EXISTING_ASIN")}
        >
          <span className="choice-card__icon"><Search size={25} /></span>
          <span className="choice-card__check">
            {draft.mode === "MATCH_EXISTING_ASIN" ? <Check size={14} /> : <Circle size={14} />}
          </span>
          <strong>Vorhandene ASIN verwenden</strong>
          <p>Per ASIN, EAN oder Titel im Amazon-Katalog suchen und ein Angebot anhängen.</p>
          <span className="choice-card__tag">Empfohlen, wenn Treffer vorhanden</span>
        </button>
        <button
          type="button"
          className={`choice-card ${draft.mode === "CREATE_NEW_ASIN" ? "is-selected" : ""}`}
          onClick={() => chooseMode("CREATE_NEW_ASIN")}
        >
          <span className="choice-card__icon"><Sparkles size={25} /></span>
          <span className="choice-card__check">
            {draft.mode === "CREATE_NEW_ASIN" ? <Check size={14} /> : <Circle size={14} />}
          </span>
          <strong>Neue ASIN erstellen</strong>
          <p>Einen neuen Amazon-Katalogartikel mit Produkttyp und Pflichtattributen anlegen.</p>
          <span className="choice-card__tag">Nur bei echtem neuen Katalogartikel</span>
        </button>
      </div>

      {draft.mode === "MATCH_EXISTING_ASIN" ? (
        <CatalogSearch selectedItem={selectedItem} onSelect={setSelectedItem} />
      ) : (
        <>
          <div className="safety-callout safety-callout--blue catalog-de-scope">
            <ShieldCheck size={19} />
            <div>
              <strong>Neue Katalogartikel werden in Version 1 zuerst auf Amazon.de angelegt.</strong>
              <p>
                Das aktuell geladene Produkttyp- und Sprachschema ist deutsch. Nach Anlage
                der deutschen ASIN kannst du sie im Aktualisierer mit wenigen Klicks nach
                IT, FR, BE, NL und IE ausrollen. Vorhandene Katalog-ASINs bleiben hier
                länderübergreifend nutzbar.
              </p>
            </div>
          </div>
          <ProductTypeSearch selectedType={productType} onSelect={setProductType} />
        </>
      )}
    </Panel>
  );
}

function CatalogSearch({
  selectedItem,
  onSelect,
}: {
  selectedItem: CatalogItem | null;
  onSelect: (item: CatalogItem, restrictions: Restriction[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [selecting, setSelecting] = useState<string | null>(null);

  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const result = await api.catalogSearch(query.trim(), "DE");
      setItems(result.items ?? []);
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setLoading(false);
    }
  };

  const select = async (item: CatalogItem) => {
    if (getBlockedFulfillmentModes(item.existingOffers, ["DE"]).length === 2) return;
    setSelecting(item.asin);
    setError(null);
    try {
      const itemRestrictions = await api.restrictions(item.asin);
      onSelect(item, itemRestrictions);
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSelecting(null);
    }
  };

  return (
    <div className="catalog-area">
      <div className="subsection-heading">
        <div>
          <h3>Amazon-Katalog durchsuchen</h3>
          <p>Suche möglichst eindeutig mit ASIN oder EAN. Quelle ist ausschließlich Amazon.</p>
        </div>
        <span className="source-chip"><Store size={14} /> Amazon-Katalog</span>
      </div>
      <form className="search-bar" onSubmit={search}>
        <Search size={19} aria-hidden />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="ASIN, EAN, UPC oder Produkttitel eingeben …"
          aria-label="Amazon-Katalog durchsuchen"
        />
        <Button type="submit" loading={loading} disabled={!query.trim()}>
          Suchen
        </Button>
      </form>
      {error && <InlineError message={error} />}
      {selectedItem && (
        <div className="selected-catalog-item">
          <span className="selected-catalog-item__check"><Check size={17} /></span>
          <CatalogThumb item={selectedItem} />
          <div>
            <span>Gewählter Katalogartikel</span>
            <strong>{selectedItem.title}</strong>
            <small>ASIN {selectedItem.asin} · {selectedItem.brand ?? "Marke nicht angegeben"}</small>
            <CatalogExistingOffers offers={selectedItem.existingOffers} />
          </div>
          <BadgeCheck size={22} />
        </div>
      )}
      {searched && !loading && !error && items.length === 0 && (
        <StatePanel
          type="empty"
          title="Kein eindeutiger Katalogtreffer"
          description="Prüfe ASIN oder EAN. Wenn der Artikel wirklich neu ist, wechsle oben zu „Neue ASIN erstellen“."
        />
      )}
      {items.length > 0 && (
        <div className="catalog-results">
          <div className="catalog-results__meta">{items.length} Treffer aus Amazon DE</div>
          {items.map((item) => {
            const selected = selectedItem?.asin === item.asin;
            const blocked = getBlockedFulfillmentModes(item.existingOffers, ["DE"]).length === 2;
            return (
              <article key={item.asin} className={`catalog-result ${selected ? "is-selected" : ""} ${blocked ? "is-blocked" : ""}`}>
                <CatalogThumb item={item} />
                <div className="catalog-result__body">
                  <strong>{item.title}</strong>
                  <span>{item.brand ?? "Marke unbekannt"} · {item.productType ?? "Produkttyp unbekannt"}</span>
                  <small>ASIN {item.asin}</small>
                  <CatalogExistingOffers offers={item.existingOffers} />
                </div>
                <Button
                  variant={selected ? "secondary" : "primary"}
                  loading={selecting === item.asin}
                  disabled={blocked}
                  onClick={() => void select(item)}
                >
                  {blocked ? "Bereits vorhanden" : selected ? "Gewählt" : "Auswählen"}
                </Button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CatalogExistingOffers({ offers }: { offers: CatalogItem["existingOffers"] }) {
  if (!offers?.length) return null;
  const blocked = getBlockedFulfillmentModes(offers, ["DE"]);

  return (
    <div className="catalog-existing-offers" role="group" aria-label="Eigene Angebote">
      <p className="catalog-existing-offers__heading">
        <CheckCircle2 size={16} aria-hidden />
        <b>Bereits Angebot von uns vorhanden</b>
      </p>
      {blocked.length > 0 && (
        <p className="catalog-existing-offers__rule">
          {blocked.length === 2
            ? "FBM und FBA sind auf Amazon DE bereits vorhanden. Ein weiterer Artikel kann dort nicht angelegt werden."
            : blocked.includes("FBM")
            ? "FBM ist auf Amazon DE bereits vorhanden und gesperrt. Du kannst nur noch ein FBA-Angebot anlegen."
            : "FBA ist auf Amazon DE bereits vorhanden und gesperrt. Du kannst nur noch ein FBM-Angebot anlegen."}
        </p>
      )}
      <ul role="list">
        {offers.map((offer) => (
          <li key={`${offer.marketplace}:${offer.fulfillmentMode}:${offer.sellerSku}`}>
            <div className="catalog-existing-offers__details">
              <div>
                <b className={`fulfillment-tag fulfillment-tag--${offer.fulfillmentMode.toLowerCase()}`}>{offer.fulfillmentMode}</b>
                {offer.fulfillmentMode === "FBA"
                  ? " · Versand durch Amazon"
                  : " · Versand durch uns"}
              </div>
              <div className="catalog-existing-offers__marketplace">Amazon {offer.marketplace}</div>
            </div>
            <code className="catalog-existing-offers__sku">SKU: {offer.sellerSku}</code>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CatalogThumb({ item }: { item: CatalogItem }) {
  return item.imageUrl ? (
    <img className="catalog-thumb" src={item.imageUrl} alt="" />
  ) : (
    <span className="catalog-thumb catalog-thumb--empty"><Box size={25} /></span>
  );
}

function ProductTypeSearch({
  selectedType,
  onSelect,
}: {
  selectedType: ProductType | null;
  onSelect: (type: ProductType) => void;
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<ProductType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      setItems(await api.productTypes(query.trim(), "DE"));
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="catalog-area">
      <div className="subsection-heading">
        <div>
          <h3>Amazon-Produkttyp festlegen</h3>
          <p>Der Produkttyp bestimmt die Pflichtfelder und Validierungsregeln des Katalogs.</p>
        </div>
        <span className="source-chip"><Sparkles size={14} /> Neue Katalogseite</span>
      </div>
      <form className="search-bar" onSubmit={search}>
        <Search size={19} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="z. B. Toys And Games, Building Blocks …"
          aria-label="Amazon-Produkttyp suchen"
        />
        <Button type="submit" loading={loading} disabled={!query.trim()}>Suchen</Button>
      </form>
      {error && <InlineError message={error} />}
      {searched && !loading && !error && items.length === 0 && (
        <StatePanel
          type="empty"
          title="Kein Produkttyp gefunden"
          description="Versuche einen allgemeineren Begriff. Die Optionen kommen direkt aus dem Amazon-Schema."
        />
      )}
      {items.length > 0 && (
        <div className="product-type-grid">
          {items.map((item) => (
            <button
              type="button"
              key={item.name}
              className={selectedType?.name === item.name ? "is-selected" : ""}
              onClick={() => onSelect(item)}
            >
              <span><PackagePlus size={18} /></span>
              <strong>{item.displayName ?? item.name}</strong>
              <small>{item.name}</small>
              {selectedType?.name === item.name && <Check size={16} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OfferStep({
  draft,
  updateDraft,
  existingOffers,
  templates,
  templatesLoading,
  productTypeDefinition,
  productTypeDefinitionLoading,
  productTypeDefinitionError,
  reloadProductTypeDefinition,
}: {
  draft: ListingDraft;
  updateDraft: (patch: Partial<ListingDraft>) => void;
  existingOffers: CatalogItem["existingOffers"];
  templates: SettingsTemplate[];
  templatesLoading: boolean;
  productTypeDefinition: ProductTypeDefinitionResponse | null;
  productTypeDefinitionLoading: boolean;
  productTypeDefinitionError: string | null;
  reloadProductTypeDefinition: () => void;
}) {
  const templateSchema = useMemo(
    () => resolveProductTypeSchema(productTypeDefinition),
    [productTypeDefinition],
  );
  const allowedTemplateAttributeKeys = useMemo(
    () =>
      templateSchema?.properties
        ? new Set(Object.keys(templateSchema.properties))
        : null,
    [templateSchema],
  );
  const applicableTemplates = useMemo(
    () =>
      templates.flatMap((template) => {
        const rawAttributes = getApplicableTemplateAttributes(
          template,
          draft.productType,
          "DE",
        );
        const attributes =
          rawAttributes && allowedTemplateAttributeKeys
            ? Object.fromEntries(
                Object.entries(rawAttributes).filter(([key]) =>
                  allowedTemplateAttributeKeys.has(key),
                ),
              )
            : rawAttributes;
        return attributes && Object.keys(attributes).length
          ? [{ template, attributes }]
          : [];
      }),
    [templates, draft.productType, allowedTemplateAttributeKeys],
  );

  const applyTemplate = (templateId: string) => {
    const applicable = applicableTemplates.find(
      (item) => item.template.id === templateId,
    );
    if (applicable) {
      updateDraft({
        attributes: { ...applicable.attributes, ...draft.attributes },
      });
    }
  };

  const fbmBlocked = getBlockedFulfillmentModes(existingOffers, draft.targetMarketplaces).includes("FBM");
  // Choosing FBA always switches the target to DE.
  const fbaBlocked = getBlockedFulfillmentModes(existingOffers, ["DE"]).includes("FBA");

  return (
    <Panel className="wizard-panel">
      <div className="wizard-panel__header">
        <div>
          <span className="section-number">02</span>
          <p className="eyebrow">Angebot konfigurieren</p>
          <h2>SKU, Versandweg und Artikeldaten</h2>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section__heading">
          <h3>Interne Angebotsdaten</h3>
          <p>Bestehende SKUs werden nie automatisch umbenannt.</p>
        </div>
        <div className="form-grid form-grid--two">
          <Field
            label="Zentrale Seller-SKU"
            hint="Wird bei der Vorschau zentral vergeben. FBA benötigt eine bestätigte FBA-Zuordnung."
          >
            <div className="input-action">
              <Input
                value={draft.sellerSku}
                readOnly
                placeholder="Wird in der Vorschau geprüft"
                autoComplete="off"
              />
            </div>
          </Field>
          <Field label="Zustand *">
            <Select
              value={draft.condition}
              onChange={(event) => updateDraft({ condition: event.target.value })}
            >
              <option value="new_new">Neu</option>
            </Select>
          </Field>
        </div>
        <SkuIdentityFields gtin={draft.gtin} billbeeId={draft.billbeeId} identityId={draft.identityId} unit={draft.skuUnit} onChange={updateDraft} />
      </div>

      <div className="form-section">
        <div className="form-section__heading">
          <h3>Fulfillment</h3>
          <p>Wähle, wer Bestellungen ausliefert. FBA-Bestand bleibt immer unangetastet.</p>
        </div>
        <div className="fulfillment-grid">
          <button
            type="button"
            className={draft.fulfillmentMode === "FBM" ? "is-selected" : ""}
            disabled={fbmBlocked}
            onClick={() => updateDraft({ fulfillmentMode: "FBM" })}
          >
            <span className="fulfillment-icon"><Truck size={24} /></span>
            <span><strong>FBM</strong><small>{fbmBlocked ? "Eigenes FBM-Angebot bereits vorhanden" : "Versand durch Playzeug"}</small></span>
            <span className="choice-card__check">
              {draft.fulfillmentMode === "FBM" ? <Check size={14} /> : <Circle size={14} />}
            </span>
          </button>
          <button
            type="button"
            className={draft.fulfillmentMode === "FBA" ? "is-selected" : ""}
            disabled={fbaBlocked}
            onClick={() =>
              updateDraft({
                fulfillmentMode: "FBA",
                targetMarketplaces: ["DE"],
                startPrices:
                  typeof draft.startPrices.DE === "number"
                    ? { DE: draft.startPrices.DE }
                    : {},
                shippingTemplates: {},
              })
            }
          >
            <span className="fulfillment-icon"><ShoppingBag size={24} /></span>
            <span><strong>FBA</strong><small>{fbaBlocked ? "Eigenes FBA-Angebot auf Amazon DE bereits vorhanden" : "Versand durch Amazon"}</small></span>
            <span className="choice-card__check">
              {draft.fulfillmentMode === "FBA" ? <Check size={14} /> : <Circle size={14} />}
            </span>
          </button>
        </div>
        <div className={`safety-callout ${draft.fulfillmentMode === "FBA" ? "safety-callout--blue" : ""}`}>
          <ShieldCheck size={19} />
          {draft.fulfillmentMode === "FBM" ? (
            <div>
              <strong>Neue FBM-Angebote starten mit Menge 0.</strong>
              <p>
                Besteht im Zielland bereits ein eigenes FBM-Angebot, wird eine weitere
                Anlage gesperrt. Bestehende Angebote bearbeitest du unter „Artikel & Angebote“.
              </p>
            </div>
          ) : (
            <div>
              <strong>FBA ist in Version 1 nur für Amazon DE vorgesehen.</strong>
              <p>
                Es wird der Kanal AMAZON_EU gesetzt, aber niemals eine Menge geschrieben.
                Auslandsangebote werden in Version 1 ausschließlich als FBM ausgerollt.
              </p>
            </div>
          )}
        </div>
      </div>

      {draft.mode === "CREATE_NEW_ASIN" && (
        <div className="form-section">
          <div className="form-section__heading form-section__heading--actions">
            <div>
              <h3>Neue Katalogdaten</h3>
              <p>Pflichtfelder werden serverseitig noch einmal gegen das Amazon-Schema geprüft.</p>
            </div>
            <Select
              aria-label="Datenvorlage anwenden"
              defaultValue=""
              disabled={templatesLoading || applicableTemplates.length === 0}
              onChange={(event) => {
                applyTemplate(event.target.value);
                event.target.value = "";
              }}
            >
              <option value="">Vorlage anwenden …</option>
              {applicableTemplates.map(({ template }) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                  {template.marketplace ? ` · ${template.marketplace}` : ""}
                </option>
              ))}
            </Select>
          </div>
          {!templatesLoading && applicableTemplates.length === 0 && templates.length > 0 && (
            <p className="template-compatibility-note">
              Vorhandene Vorlagen enthalten keine passenden strukturierten{" "}
              <code>values.attributes</code> für {draft.productType} / DE und werden deshalb
              nicht angeboten.
            </p>
          )}
          <ProductTypeAttributesForm
            productType={draft.productType ?? "PRODUCT"}
            definition={productTypeDefinition}
            loading={productTypeDefinitionLoading}
            error={productTypeDefinitionError}
            attributes={draft.attributes}
            onChange={(attributes) => updateDraft({ attributes })}
            onRetry={reloadProductTypeDefinition}
          />
        </div>
      )}
    </Panel>
  );
}

function MarketplaceStep({
  draft,
  updateDraft,
  settings,
  settingsError,
}: {
  draft: ListingDraft;
  updateDraft: (patch: Partial<ListingDraft>) => void;
  settings: AppSettings | null;
  settingsError: string | null;
}) {
  const [shipping, setShipping] = useState<
    Partial<Record<MarketplaceCode, ShippingTemplate[]>>
  >({});
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState<string | null>(null);

  useEffect(() => {
    if (draft.fulfillmentMode !== "FBM" || draft.targetMarketplaces.length === 0) return;
    let active = true;
    setShippingLoading(true);
    setShippingError(null);
    Promise.all(
      draft.targetMarketplaces.map(async (code) => [code, await api.shippingTemplates(code)] as const),
    )
      .then((pairs) => {
        if (active) setShipping(Object.fromEntries(pairs));
      })
      .catch((error) => {
        if (active) setShippingError(getApiError(error));
      })
      .finally(() => {
        if (active) setShippingLoading(false);
      });
    return () => {
      active = false;
    };
  }, [draft.fulfillmentMode, draft.targetMarketplaces.join(",")]);

  const targetCompliance = settings?.compliance ?? {};
  const disabled = MARKETPLACE_CODES.filter((code) => {
    if (draft.mode === "CREATE_NEW_ASIN" && code !== "DE") return true;
    if (draft.fulfillmentMode === "FBA" && code !== "DE") return true;
    const gate = targetCompliance[code];
    return code !== "DE" && gate && gate.enabledForWrites === false;
  });

  const setCountries = (value: MarketplaceCode | MarketplaceCode[]) => {
    const codes =
      draft.mode === "CREATE_NEW_ASIN"
        ? (["DE"] as MarketplaceCode[])
        : (value as MarketplaceCode[]);
    const nextPrices = { ...draft.startPrices };
    const nextTemplates = { ...draft.shippingTemplates };
    Object.keys(nextPrices).forEach((code) => {
      if (!codes.includes(code as MarketplaceCode)) delete nextPrices[code as MarketplaceCode];
    });
    Object.keys(nextTemplates).forEach((code) => {
      if (!codes.includes(code as MarketplaceCode)) delete nextTemplates[code as MarketplaceCode];
    });
    updateDraft({
      targetMarketplaces: codes,
      startPrices: nextPrices,
      shippingTemplates: nextTemplates,
    });
  };

  const suggestForeign = () => {
    const dePrice = draft.startPrices.DE;
    if (!dePrice) return;
    const prices = { ...draft.startPrices };
    draft.targetMarketplaces.forEach((code) => {
      if (code !== "DE") prices[code] = calculateForeignPrice(dePrice, code, settings);
    });
    updateDraft({ startPrices: prices, sourceDePrice: dePrice });
  };

  const dePrice = draft.startPrices.DE;

  return (
    <Panel className="wizard-panel">
      <div className="wizard-panel__header">
        <div>
          <span className="section-number">03</span>
          <p className="eyebrow">Länder-Rollout</p>
          <h2>Marktplätze, Versandvorlagen und Startpreise</h2>
        </div>
      </div>

      {settingsError && (
        <InlineError
          message={`Preiseinstellungen konnten nicht geladen werden: ${settingsError}`}
        />
      )}

      <div className="form-section">
        <div className="form-section__heading">
          <h3>Zielländer auswählen</h3>
          <p>Gesperrte Compliance-Länder können erst nach Freigabe beschrieben werden.</p>
        </div>
        <MarketplaceSelector
          value={draft.targetMarketplaces}
          onChange={setCountries}
          multiple
          disabledCodes={disabled}
          label=""
        />
        {disabled.length > 0 && (
          <p className="field__hint">
            {draft.mode === "CREATE_NEW_ASIN"
              ? "Neue ASINs werden in Version 1 ausschließlich auf DE angelegt. Der Auslands-Rollout folgt anschließend im Aktualisierer."
              : draft.fulfillmentMode === "FBA"
              ? "FBA in Version 1 nur DE. Für IT, FR, BE, NL und IE bitte FBM wählen."
              : `${disabled.join(", ")} durch Compliance-Ampel gesperrt. Freigabe unter Einstellungen.`}
          </p>
        )}
        {draft.mode === "CREATE_NEW_ASIN" && (
          <div className="safety-callout safety-callout--blue create-asin-rollout-note">
            <ShieldCheck size={18} />
            <div>
              <strong>Schritt 1: deutsche ASIN anlegen</strong>
              <p>
                Hier wird nur DE übermittelt. Sobald Amazon die ASIN angelegt hat, öffnest
                du den Aktualisierer und rollst dasselbe Katalogprodukt kontrolliert nach
                IT, FR, BE, NL und IE aus.
              </p>
            </div>
          </div>
        )}
      </div>

      {draft.targetMarketplaces.length === 0 ? (
        <StatePanel
          type="empty"
          title="Noch kein Zielland gewählt"
          description="Wähle mindestens einen Marketplace, um Preis und Versand zu konfigurieren."
        />
      ) : (
        <div className="country-config-list">
          <div className="country-config-list__header">
            <div>
              <h3>Länderkonfiguration</h3>
              <p>Preis wird nur beim Erstellen oder Ausrollen an Amazon übermittelt.</p>
            </div>
            {draft.targetMarketplaces.includes("DE") &&
              draft.targetMarketplaces.some((code) => code !== "DE") && (
                <Button
                  variant="secondary"
                  onClick={suggestForeign}
                  disabled={!dePrice}
                  icon={<Sparkles size={16} />}
                >
                  Auslandspreise berechnen
                </Button>
              )}
          </div>

          {shippingError && <InlineError message={shippingError} />}
          {draft.targetMarketplaces.map((code) => {
            const meta = MARKETPLACE_META[code];
            const price = draft.startPrices[code];
            const minimum =
              code !== "DE" && dePrice ? calculateForeignPrice(dePrice, code, settings) : null;
            const priceInvalid =
              code !== "DE" &&
              typeof price === "number" &&
              price > 0 &&
              typeof minimum === "number" &&
              price < minimum;
            const gate = targetCompliance[code];
            return (
              <div key={code} className="country-config">
                <div className="country-config__identity">
                  <MarketplaceMark code={code} decorative />
                  <div>
                    <strong>{meta.label}</strong>
                    <span>{meta.vat}% MwSt.</span>
                  </div>
                  {gate?.enabledForWrites !== false ? (
                    <StatusPill status="success" label="Freigegeben" />
                  ) : (
                    <StatusPill status="restricted" label="Gesperrt" />
                  )}
                </div>
                <Field
                  label={code === "DE" ? "DE-Startpreis *" : "Auslands-Startpreis *"}
                  hint={
                    code === "DE"
                      ? "Basis für die Auslandspreis-Schutzregel."
                      : minimum
                        ? `Mindestens ${formatMoney(minimum)} und höher als DE.`
                        : "DE mit auswählen und Basispreis setzen, um automatisch zu prüfen."
                  }
                  error={priceInvalid ? `Mindestens ${formatMoney(minimum ?? undefined)} erforderlich.` : undefined}
                >
                  <div className="money-input">
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      inputMode="decimal"
                      value={price ?? ""}
                      onChange={(event) =>
                        updateDraft({
                          startPrices: {
                            ...draft.startPrices,
                            [code]: event.target.value ? Number(event.target.value) : undefined,
                          },
                          sourceDePrice:
                            code === "DE" && event.target.value
                              ? Number(event.target.value)
                              : draft.sourceDePrice,
                        })
                      }
                      placeholder="0,00"
                    />
                    <span>EUR</span>
                  </div>
                </Field>
                {draft.fulfillmentMode === "FBM" ? (
                  <Field label="Amazon-Versandvorlage *">
                    <Select
                      value={draft.shippingTemplates[code] ?? ""}
                      disabled={shippingLoading}
                      onChange={(event) =>
                        updateDraft({
                          shippingTemplates: {
                            ...draft.shippingTemplates,
                            [code]: event.target.value,
                          },
                        })
                      }
                    >
                      <option value="">
                        {shippingLoading ? "Vorlagen werden geladen …" : "Versandvorlage wählen …"}
                      </option>
                      {(shipping[code] ?? []).map((template) => (
                        <option
                          key={template.templateId ?? template.id ?? template.name}
                          value={template.templateId ?? template.id ?? template.name}
                        >
                          {template.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : (
                  <div className="fba-readonly">
                    <ShoppingBag size={17} />
                    <span><strong>AMAZON_EU</strong><small>Keine Menge wird gesendet.</small></span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {draft.mode !== "CREATE_NEW_ASIN" && (
        <div className="price-policy">
          <Info size={19} />
          <div>
            <strong>Auslandspreis-Schutz</strong>
            <p>
              Standard: mindestens 5% oder 2,50 € über DE, Zielland-MwSt. sowie
              konfigurierte Versand-/Gebührenpuffer, gerundet auf ,95. Das ist nur der
              Startpreis – danach bleibt der Repricer zuständig.
            </p>
          </div>
        </div>
      )}
    </Panel>
  );
}

function PreviewStep({
  draft,
  preview,
  previewing,
  submitting,
  submitResult,
  blocked,
  onPreview,
  onSubmit,
  onReset,
}: {
  draft: ListingDraft;
  preview: ListingPreview | null;
  previewing: boolean;
  submitting: boolean;
  submitResult: { jobId: string; status: string } | null;
  blocked: boolean;
  onPreview: () => Promise<void>;
  onSubmit: () => Promise<void>;
  onReset: () => void;
}) {
  if (submitResult) {
    return (
      <Panel className="wizard-panel submit-success">
        <span className="submit-success__icon"><UploadCloud size={31} /></span>
        <StatusPill status="queued" label="Zur Verarbeitung eingereiht" />
        <h2>Upload wurde sicher angenommen</h2>
        <p>
          Amazon verarbeitet die Änderung im Hintergrund. „Übermittelt“ oder „akzeptiert“
          bedeutet noch nicht „live“ – der Job prüft den sichtbaren Zustand anschließend.
        </p>
        <div className="submit-success__job">
          <span>Job-ID</span><code>{submitResult.jobId}</code>
          <span>Status</span><strong>{submitResult.status}</strong>
        </div>
        <div className="submit-success__actions">
          <Button onClick={onReset} icon={<PackagePlus size={17} />}>
            Weiteren Artikel hochladen
          </Button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel className="wizard-panel">
      <div className="wizard-panel__header">
        <div>
          <span className="section-number">04</span>
          <p className="eyebrow">Serverseitige Validierung</p>
          <h2>Exakte Vorschau vor dem Senden</h2>
        </div>
        {preview && (
          <span className="preview-expiry">
            Vorschau gültig bis {new Date(preview.expiresAt).toLocaleTimeString("de-DE", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
      </div>

      {!preview ? (
        <div className="preview-gate">
          <span className="preview-gate__icon"><FileDiff size={28} /></span>
          <h3>Vorschau jetzt erzeugen</h3>
          <p>
            Der API Server prüft Amazon-Beschränkungen, Compliance, Preise,
            Versandvorlagen und für jedes Land, ob die SKU bereits existiert.
          </p>
          <ul>
            <li><CheckCircle2 size={16} /> Keine Amazon-Schreibaktion in diesem Schritt</li>
            <li><CheckCircle2 size={16} /> Bestehende Angebote ohne Mengenfeld</li>
            <li><CheckCircle2 size={16} /> Einmaliger Bestätigungstoken gegen Doppelübertragung</li>
          </ul>
          <Button
            loading={previewing}
            disabled={blocked}
            onClick={() => void onPreview()}
            icon={<ShieldCheck size={17} />}
          >
            Sicher prüfen
          </Button>
        </div>
      ) : (
        <div className="preview-content">
          <div className="preview-summary">
            <div><span>SKU</span><strong>{draft.sellerSku}</strong></div>
            <div><span>Modus</span><strong>{draft.mode === "MATCH_EXISTING_ASIN" ? "Katalog-ASIN" : "Neue ASIN"}</strong></div>
            <div><span>Versand</span><strong>{draft.fulfillmentMode}</strong></div>
            <div><span>Länder</span><strong>{draft.targetMarketplaces.join(", ")}</strong></div>
          </div>

          {(preview.errors?.length ?? 0) > 0 && (
            <div className="message-list message-list--error">
              {preview.errors?.map((message, index) => (
                <span key={`${message}-${index}`}><XCircle size={16} /> {message}</span>
              ))}
            </div>
          )}
          {(preview.warnings?.length ?? 0) > 0 && (
            <div className="message-list message-list--warning">
              {preview.warnings?.map((message, index) => (
                <span key={`${message}-${index}`}><AlertTriangle size={16} /> {message}</span>
              ))}
            </div>
          )}

          <div className="marketplace-preview-list">
            {preview.marketplaces.map((marketplace) => (
              <details key={marketplace.code} className="marketplace-preview" open>
                <summary>
                  <MarketplaceMark code={marketplace.code} decorative />
                  <div>
                    <strong>{MARKETPLACE_META[marketplace.code].label}</strong>
                    <span>
                      {marketplace.exists ? "SKU besteht bereits" : "Neue SKU"} · {marketplace.operation}
                    </span>
                  </div>
                  <StatusPill
                    status={marketplace.errors.length ? "error" : marketplace.warnings.length ? "warning" : "success"}
                    label={marketplace.errors.length ? `${marketplace.errors.length} Fehler` : marketplace.warnings.length ? `${marketplace.warnings.length} Hinweise` : "Bereit"}
                  />
                  <ChevronDown size={17} />
                </summary>
                <div className="marketplace-preview__body">
                  <div className="availability-rule">
                    {marketplace.exists ? (
                      <>
                        <ShieldCheck size={17} />
                        <span><strong>Bestand bleibt unverändert</strong> · fulfillment_availability wird vollständig ausgelassen.</span>
                      </>
                    ) : draft.fulfillmentMode === "FBM" ? (
                      <>
                        <PackageCheck size={17} />
                        <span><strong>Neues FBM-Angebot</strong> · Startmenge 0 wird einmalig gesetzt.</span>
                      </>
                    ) : (
                      <>
                        <ShoppingBag size={17} />
                        <span><strong>FBA AMAZON_EU</strong> · Es wird keine Menge übertragen.</span>
                      </>
                    )}
                  </div>
                  {marketplace.errors.map((message, index) => (
                    <p className="preview-message preview-message--error" key={`error-${index}`}>
                      <XCircle size={15} /> {message}
                    </p>
                  ))}
                  {marketplace.warnings.map((message, index) => (
                    <p className="preview-message preview-message--warning" key={`warning-${index}`}>
                      <AlertTriangle size={15} /> {message}
                    </p>
                  ))}
                  <div className="diff-table-wrap">
                    <table className="diff-table">
                      <thead><tr><th>Feld</th><th>Vorher</th><th>Nachher</th></tr></thead>
                      <tbody>
                        {marketplace.diff.length ? (
                          marketplace.diff.map((diff, index) => (
                            <tr key={`${diff.path ?? diff.field}-${index}`}>
                              <td><code>{diff.path ?? diff.field ?? "Änderung"}</code></td>
                              <td>{renderDiffValue(diff.before)}</td>
                              <td className="diff-after">{renderDiffValue(diff.after)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={3} className="empty-cell">Keine abweichenden Felder.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            ))}
          </div>

          <div className="confirmation-box">
            <ShieldCheck size={22} />
            <div>
              <strong>Explizite Bestätigung</strong>
              <p>
                Mit „Jetzt an Amazon senden“ bestätigst du genau diese Vorschau. Der
                Bestätigungstoken ist einmalig; Doppelklicks erzeugen keinen zweiten Upload.
              </p>
            </div>
            <Button
              loading={submitting}
              disabled={blocked || !preview.canSubmit}
              onClick={() => void onSubmit()}
              icon={<UploadCloud size={17} />}
            >
              Jetzt an Amazon senden
            </Button>
          </div>
        </div>
      )}
    </Panel>
  );
}

function SummaryRow({
  label,
  value,
  complete,
}: {
  label: string;
  value: string;
  complete: boolean;
}) {
  return (
    <div className="summary-row">
      <span>{label}</span>
      <strong>{value}</strong>
      {complete ? <CheckCircle2 size={15} /> : <Circle size={15} />}
    </div>
  );
}

function calculateMinimumForeignPrice(dePrice: number, settings: AppSettings | null) {
  const percent = (settings?.priceGuard?.minimumPercent ?? 5) / 100;
  const absolute = settings?.priceGuard?.minimumAbsoluteEur ?? 2.5;
  return Math.max(dePrice * (1 + percent), dePrice + absolute);
}

function calculateForeignPrice(
  dePrice: number,
  code: MarketplaceCode,
  settings: AppSettings | null,
) {
  const deVat = MARKETPLACE_META.DE.vat / 100;
  const targetVat = MARKETPLACE_META[code].vat / 100;
  const buffer = settings?.marketplaceBuffers?.[code];
  const shipping = buffer?.shippingEur ?? 0;
  const fee = (buffer?.feePercent ?? 0) / 100;
  const net = dePrice / (1 + deVat);
  const vatAdjusted = (net + shipping) * (1 + targetVat);
  const feeAdjusted = fee >= 1 ? Number.POSITIVE_INFINITY : vatAdjusted / (1 - fee);
  const guarded = Math.max(feeAdjusted, calculateMinimumForeignPrice(dePrice, settings));
  return roundTo95(guarded);
}

function roundTo95(value: number) {
  const base = Math.floor(value);
  const candidate = base + 0.95;
  return Number((candidate >= value ? candidate : base + 1.95).toFixed(2));
}

function renderDiffValue(value: unknown) {
  if (value === null || value === undefined || value === "") return <span className="diff-empty">nicht gesetzt</span>;
  if (typeof value === "object") return <code>{JSON.stringify(value)}</code>;
  return String(value);
}
